import { ChangeEvent, ReactNode } from "react";
import { ViewItem } from "../../../../Components/Views/ViewItem";
import { MailHtmlFrame, sanitizeEmailHtml } from "../../../../Components/Views/MailHtmlFrame";
import { controllers } from "../../../../Controllers/Controllers";
import { navigator } from "../../../../Controllers/Navigator";
import { STATUS_LABELS, LeadCategoryType, LeadType, LeadsPagination } from "../../../../DataTypes/leads";
import { EmailTemplatePreviewResult, EmailTemplateType } from "../../../../DataTypes/templates";
import { SourceFilter, SourceSelectView } from "../../../UIComponents/SourceSelectView";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import '../Mail/mail-styles.css'
import '../../Settings/settings-styles.css'
import '../TemplateForm/template-form-styles.css'
import '../Compose/compose-styles.css'
import './compose-campaign-styles.css'

const PER_PAGE = 25
const SEARCH_DEBOUNCE_MS = 350

const getRecipientEmail = (lead: LeadType) => lead.decision_maker_email || lead.company_email || ''

const SOURCE_FILTER_VALUES: SourceFilter[] = ['all', 'outreach', 'research']

const parseFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search)
    const page = Number(params.get('page'))
    const status = params.get('status') || 'all'
    const source = params.get('source') || 'all'
    return {
        page: Number.isInteger(page) && page > 0 ? page : 1,
        status: status in STATUS_LABELS ? status : 'all',
        search: params.get('search') || '',
        source: (SOURCE_FILTER_VALUES as string[]).includes(source) ? source as SourceFilter : 'all',
        categoryId: params.get('category') || 'all',
    }
}

// A custom signature is only a sending problem if it's actually missing
// the unsubscribe token — checked here from the backend preview's own
// placeholders_used list (the same regex-based detection the backend
// uses for real sends) rather than re-parsing raw HTML on the client.
const customSignatureMissingUnsubscribe = (preview: EmailTemplatePreviewResult | null): boolean => {
    if (!preview?.signature) return false
    if (preview.signature.source !== 'custom') return false
    return !preview.signature.placeholders_used.includes('unsubscribe_url')
}

type ComposeCampaignPageState = {
    leads: LeadType[];
    pagination: LeadsPagination | null;
    leadsLoading: boolean;
    leadsError: string | null;
    needsAuth: boolean;
    search: string;
    source: SourceFilter;
    status: string;
    categories: LeadCategoryType[];
    categoryId: string;
    page: number;

    // Kept as full lead objects (not just ids) so the "Preview using" picker
    // and the selected count still make sense after paginating away from
    // the page a given lead was checked on.
    selectedLeads: LeadType[];
    queuedLeadIds: number[];
    justSent: boolean;

    templates: EmailTemplateType[];
    selectedTemplateId: string;
    previewLeadId: string;
    templateLoading: boolean;
    templateError: string | null;
    templatePreview: EmailTemplatePreviewResult | null;

    sending: boolean;
    sendError: string | null;
    sendMessage: string | null;
}

class ComposeCampaignPage extends ViewItem {
    state: ComposeCampaignPageState;
    searchTimer: any = null;

    constructor(props: any) {
        super(props)
        const urlFilters = parseFiltersFromUrl()
        this.state = {
            leads: [],
            pagination: null,
            leadsLoading: true,
            leadsError: null,
            needsAuth: false,
            categories: [],
            ...urlFilters,

            selectedLeads: [],
            queuedLeadIds: [],
            justSent: false,

            templates: [],
            selectedTemplateId: '',
            previewLeadId: '',
            templateLoading: false,
            templateError: null,
            templatePreview: null,

            sending: false,
            sendError: null,
            sendMessage: null,
        }
    }

    componentDidMount(): void {
        this.syncUrlWithState()
        this.loadLeads()
        this.loadTemplates()
        this.loadCategories()
    }

    componentWillUnmount(): void {
        if (this.searchTimer) clearTimeout(this.searchTimer)
    }

    syncUrlWithState = () => {
        const { page, status, search, source, categoryId } = this.state
        const params = new URLSearchParams()
        if (page > 1) params.set('page', String(page))
        if (status !== 'all') params.set('status', status)
        if (search) params.set('search', search)
        if (source !== 'all') params.set('source', source)
        if (categoryId !== 'all') params.set('category', categoryId)
        const qs = params.toString()
        const newUrl = window.location.pathname + (qs ? `?${qs}` : '')
        window.history.replaceState(null, '', newUrl)
    }

    applyFilters = () => {
        this.syncUrlWithState()
        this.loadLeads()
    }

    loadTemplates = async () => {
        if (!controllers().auth.userToken) return
        const response = await controllers().mail.getTemplates()
        if (response && response.statusCode === 200) {
            this.setState({ templates: response.data?.templates || [] })
        }
    }

    loadCategories = async () => {
        const response = await controllers().outreach.getCategories()
        if (response && response.statusCode === 200) {
            this.setState({ categories: response.data?.categories || [] })
        }
    }

    loadLeads = async () => {
        const { search, page, source, status, categoryId } = this.state
        this.setState({ leadsLoading: true, leadsError: null, needsAuth: false })
        if (!controllers().auth.userToken) {
            this.setState({ leadsLoading: false, needsAuth: true })
            return
        }
        const response = await controllers().outreach.getLeads({
            search: search || undefined,
            page,
            per_page: PER_PAGE,
            has_contact_email: true,
            is_from_research: source === 'all' ? undefined : source === 'research',
            status: status !== 'all' ? status : undefined,
            category_id: categoryId !== 'all' ? Number(categoryId) : undefined,
        })
        if (!response || response.statusCode !== 200) {
            this.setState({ leadsLoading: false, leadsError: response?.error || 'Failed to load leads.' })
            return
        }
        this.setState({
            leads: response.data?.leads || [],
            pagination: response.data?.pagination || null,
            leadsLoading: false,
        })
    }

    handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        this.setState({ search: value })
        if (this.searchTimer) clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
            this.setState({ page: 1 }, this.applyFilters)
        }, SEARCH_DEBOUNCE_MS)
    }

    handleSourceChange = (value: SourceFilter) => {
        this.setState({ source: value, page: 1 }, this.applyFilters)
    }

    handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ status: e.target.value, page: 1 }, this.applyFilters)
    }

    handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ categoryId: e.target.value, page: 1 }, this.applyFilters)
    }

    goToPage = (page: number) => {
        const { pagination } = this.state
        if (pagination && (page < 1 || page > pagination.total_pages)) return
        this.setState({ page }, this.applyFilters)
    }

    toggleLead = (lead: LeadType) => {
        this.setState((prev: ComposeCampaignPageState) => {
            const isSelected = prev.selectedLeads.some(l => l.id === lead.id)
            const selectedLeads = isSelected
                ? prev.selectedLeads.filter(l => l.id !== lead.id)
                : [...prev.selectedLeads, lead]
            const previewLeadId = selectedLeads.some(l => l.id === Number(prev.previewLeadId))
                ? prev.previewLeadId
                : (selectedLeads[0] ? String(selectedLeads[0].id) : '')
            return { selectedLeads, previewLeadId }
        }, this.maybeReloadPreview)
    }

    toggleSelectAllOnPage = () => {
        const { leads, selectedLeads } = this.state
        const allOnPageSelected = leads.length > 0 && leads.every(lead => selectedLeads.some(l => l.id === lead.id))
        this.setState((prev: ComposeCampaignPageState) => {
            let nextSelected: LeadType[]
            if (allOnPageSelected) {
                const pageIds = new Set(leads.map(l => l.id))
                nextSelected = prev.selectedLeads.filter(l => !pageIds.has(l.id))
            } else {
                const existingIds = new Set(prev.selectedLeads.map(l => l.id))
                nextSelected = [...prev.selectedLeads, ...leads.filter(l => !existingIds.has(l.id))]
            }
            const previewLeadId = nextSelected.some(l => l.id === Number(prev.previewLeadId))
                ? prev.previewLeadId
                : (nextSelected[0] ? String(nextSelected[0].id) : '')
            return { selectedLeads: nextSelected, previewLeadId }
        }, this.maybeReloadPreview)
    }

    handleTemplateChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ selectedTemplateId: e.target.value, templateError: null, templatePreview: null }, this.maybeReloadPreview)
    }

    handlePreviewLeadChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ previewLeadId: e.target.value }, this.maybeReloadPreview)
    }

    maybeReloadPreview = () => {
        const { selectedTemplateId, previewLeadId } = this.state
        if (selectedTemplateId && previewLeadId) {
            this.loadTemplatePreview(Number(selectedTemplateId), Number(previewLeadId))
        } else {
            this.setState({ templatePreview: null })
        }
    }

    // Fetches subject/body AND signature together in one call — the
    // signature block rendered below comes straight from this response's
    // `.signature` field rather than being reconstructed on the client.
    // The trailing `true` tells the backend this is a CAMPAIGN preview
    // (see mail_views.preview_email_template's for_campaign flag), so
    // the signature renders using the main account's info, matching
    // what tasks.send_lead_outreach_email actually sends — as opposed
    // to TemplateFormPage's preview, which omits this flag and shows
    // the actual individual sender instead.
    loadTemplatePreview = async (templateId: number, leadId: number) => {
        this.setState({ templateLoading: true, templateError: null })
        const response = await controllers().mail.previewTemplate(templateId, leadId, true)
        if (!response || response.statusCode !== 200) {
            this.setState({ templateLoading: false, templateError: response?.error || 'Failed to load template preview.' })
            return
        }
        this.setState({ templateLoading: false, templatePreview: response.data })
    }

    handleSend = async () => {
        const { selectedTemplateId, selectedLeads, templatePreview } = this.state
        if (!selectedTemplateId) {
            this.setState({ sendError: 'Select a template first.' })
            return
        }
        if (!selectedLeads.length) {
            this.setState({ sendError: 'Select at least one lead.' })
            return
        }
        if (customSignatureMissingUnsubscribe(templatePreview)) {
            this.setState({
                sendError: "This template's custom signature doesn't include an unsubscribe link — fix it on the template before sending to real recipients.",
            })
            return
        }

        this.setState({ sending: true, sendError: null, sendMessage: null })
        const leadIds = selectedLeads.map(l => l.id)
        const response = await controllers().mail.triggerCampaignSend(Number(selectedTemplateId), leadIds)
        if (!response || response.statusCode !== 200) {
            this.setState({ sending: false, sendError: response?.error || 'Failed to queue campaign.' })
            return
        }
        this.setState((prev: ComposeCampaignPageState) => ({
            sending: false,
            justSent: true,
            sendMessage: response.message || `Campaign queued for ${leadIds.length} lead(s).`,
            queuedLeadIds: Array.from(new Set([...prev.queuedLeadIds, ...leadIds])),
        }))
    }

    handleStartAnother = () => {
        this.setState({
            justSent: false,
            sendError: null,
            sendMessage: null,
            selectedLeads: [],
            previewLeadId: '',
            templatePreview: null,
        })
    }

    renderPagination = () => {
        const { pagination, page } = this.state
        if (!pagination || pagination.total_pages <= 1) return null
        return (
            <div className="outreach-pagination compose-pagination">
                <span className="outreach-pagination-info">
                    Page {pagination.current_page} of {pagination.total_pages}
                </span>
                <div className="outreach-pagination-controls">
                    <button className="outreach-pagination-btn" disabled={!pagination.has_previous} onClick={() => this.goToPage(page - 1)}>
                        Previous
                    </button>
                    <button className="outreach-pagination-btn" disabled={!pagination.has_next} onClick={() => this.goToPage(page + 1)}>
                        Next
                    </button>
                </div>
            </div>
        )
    }

    renderListToolbar = () => {
        const { leads, selectedLeads } = this.state
        const allOnPageSelected = leads.length > 0 && leads.every(lead => selectedLeads.some(l => l.id === lead.id))
        return (
            <div className="campaign-list-toolbar">
                <label className="outreach-filter-checkbox">
                    <input type="checkbox" checked={allOnPageSelected} onChange={this.toggleSelectAllOnPage} />
                    Select all on page
                </label>
                <span className="outreach-muted campaign-selected-count">
                    {selectedLeads.length} selected
                </span>
            </div>
        )
    }

    renderLeadsList = () => {
        const { leads, selectedLeads, queuedLeadIds } = this.state
        return (
            <div className="mail-list">
                {leads.map(lead => {
                    const recipient = getRecipientEmail(lead)
                    const selected = selectedLeads.some(l => l.id === lead.id)
                    return (
                        <div
                            key={lead.id}
                            className={`mail-list-item ${selected ? 'mail-list-item-active' : ''}`}
                            onClick={() => this.toggleLead(lead)}
                        >
                            <div className="mail-list-item-top">
                                <span className="mail-list-item-sender campaign-lead-label">
                                    <input
                                        type="checkbox"
                                        className="campaign-lead-checkbox"
                                        checked={selected}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => this.toggleLead(lead)}
                                    />
                                    {queuedLeadIds.includes(lead.id) && <span className="compose-sent-check" title="Queued this session">✓</span>}
                                    {lead.company_name}
                                </span>
                                <span className={`compose-status-chip lead-status-${lead.status}`}>
                                    {STATUS_LABELS[lead.status] || lead.status}
                                </span>
                            </div>
                            <div className="mail-list-item-subject">
                                {lead.decision_maker_name || 'No contact name'}
                            </div>
                            <div className={`mail-list-item-snippet ${!recipient ? 'compose-no-email' : ''}`}>
                                {recipient || 'No email on file'}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    renderCampaignPanel = () => {
        const {
            selectedLeads, justSent, sending, sendError, sendMessage,
            templates, selectedTemplateId, previewLeadId, templateLoading, templateError, templatePreview,
        } = this.state

        if (justSent) {
            return (
                <div className="mail-thread-panel compose-panel compose-success-panel">
                    <div className="compose-success-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                    </div>
                    <h2 className="compose-success-title">Campaign queued</h2>
                    <p className="compose-success-text">{sendMessage}</p>
                    <div className="compose-success-actions">
                        <button className="outreach-add-btn" onClick={this.handleStartAnother}>
                            Start Another Campaign
                        </button>
                    </div>
                </div>
            )
        }

        if (!selectedLeads.length) {
            return (
                <div className="mail-thread-panel mail-thread-empty">
                    <span className="outreach-muted">Select leads on the left to build a campaign.</span>
                </div>
            )
        }

        const signatureBlocked = customSignatureMissingUnsubscribe(templatePreview)

        return (
            <div className="mail-thread-panel compose-panel">
                <div className="mail-thread-header">
                    <div className="mail-thread-header-info">
                        <h2 className="mail-thread-subject">New Campaign</h2>
                        <p className="mail-thread-participants">to {selectedLeads.length} lead{selectedLeads.length === 1 ? '' : 's'}</p>
                    </div>
                    <div className="mail-thread-header-actions">
                        <select
                            className="outreach-status-select compose-template-select"
                            value={selectedTemplateId}
                            onChange={this.handleTemplateChange}
                            disabled={sending}
                        >
                            <option value="">Select a template</option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {sendError && <div className="category-form-error">{sendError}</div>}
                {sendMessage && (
                    <div className="outreach-banner outreach-banner-info settings-email-success">
                        <span className="outreach-banner-text">{sendMessage}</span>
                    </div>
                )}

                {signatureBlocked && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">
                            This template's custom signature doesn't include an unsubscribe link — recipients would have no way to opt out. Fix it on the template before sending.
                        </span>
                        <button className="outreach-banner-action" onClick={() => navigator().navigate('/outreach/templates')}>
                            Manage Templates
                        </button>
                    </div>
                )}

                {!selectedTemplateId && (
                    <p className="outreach-muted template-preview-empty">Select a template above to preview the campaign.</p>
                )}

                {selectedTemplateId && (
                    <>
                        <div className="category-form-group">
                            <label className="category-form-label">Preview using</label>
                            <select
                                className="category-form-input"
                                value={previewLeadId}
                                onChange={this.handlePreviewLeadChange}
                                disabled={sending}
                            >
                                {selectedLeads.map(lead => (
                                    <option key={lead.id} value={lead.id}>{lead.company_name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="settings-signature-preview compose-signature-preview">
                            <span className="settings-signature-preview-label">Preview</span>
                            {templateLoading && (
                                <div className="outreach-state">
                                    <div className="outreach-spinner" />
                                </div>
                            )}
                            {!templateLoading && templateError && (
                                <div className="outreach-banner outreach-banner-error">
                                    <span className="outreach-banner-text">{templateError}</span>
                                </div>
                            )}
                            {!templateLoading && !templateError && templatePreview && (
                                <>
                                    {templatePreview.unrecognized_placeholders.length > 0 && (
                                        <div className="outreach-banner outreach-banner-error">
                                            <span className="outreach-banner-text">
                                                Unrecognized placeholder{templatePreview.unrecognized_placeholders.length === 1 ? '' : 's'}: {templatePreview.unrecognized_placeholders.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                                            </span>
                                        </div>
                                    )}
                                    <div className="template-preview-subject">
                                        <span className="settings-signature-preview-label">Subject</span>
                                        <p>{templatePreview.subject || <span className="outreach-muted">(none)</span>}</p>
                                    </div>
                                    {templatePreview.body_html ? (
                                        <MailHtmlFrame html={sanitizeEmailHtml(templatePreview.body_html)} />
                                    ) : templatePreview.body_text ? (
                                        <div className="template-preview-text">
                                            <span className="settings-signature-preview-label">Text Body</span>
                                            <pre>{templatePreview.body_text}</pre>
                                        </div>
                                    ) : (
                                        <p className="outreach-muted">No body content.</p>
                                    )}

                                    {templatePreview.signature && (
                                        <div className="template-preview-signature">
                                            <span className="settings-signature-preview-label">
                                                Signature ({templatePreview.signature.source === 'custom' ? 'custom for this template' : 'shared'})
                                            </span>

                                            {templatePreview.signature.unrecognized_placeholders.length > 0 && (
                                                <div className="outreach-banner outreach-banner-error">
                                                    <span className="outreach-banner-text">
                                                        Unrecognized placeholder{templatePreview.signature.unrecognized_placeholders.length === 1 ? '' : 's'}: {templatePreview.signature.unrecognized_placeholders.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                                                    </span>
                                                </div>
                                            )}

                                            {templatePreview.signature.rendered_html ? (
                                                <MailHtmlFrame html={sanitizeEmailHtml(templatePreview.signature.rendered_html)} />
                                            ) : (
                                                <p className="outreach-muted">No signature configured.</p>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                <div className="settings-email-actions compose-send-actions">
                    <button
                        className="outreach-add-btn"
                        onClick={this.handleSend}
                        disabled={sending || !selectedTemplateId || templateLoading || signatureBlocked}
                    >
                        {sending ? 'Queuing...' : `Send Campaign to ${selectedLeads.length} Lead${selectedLeads.length === 1 ? '' : 's'}`}
                    </button>
                </div>
            </div>
        )
    }

    render(): ReactNode {
        const { leads, leadsLoading, leadsError, needsAuth, search, source, status, categories, categoryId } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Campaign</h1>
                        <p className="outreach-subtitle">Send a template to many leads at once</p>
                    </div>
                    <div className="outreach-toolbar">
                        <input
                            type="text"
                            className="outreach-search"
                            placeholder="Search leads..."
                            value={search}
                            onChange={this.handleSearchChange}
                        />
                        <select className="outreach-status-select" value={status} onChange={this.handleStatusChange}>
                            <option value="all">All statuses</option>
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <select className="outreach-status-select" value={categoryId} onChange={this.handleCategoryChange}>
                            <option value="all">All categories</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <SourceSelectView
                            source={source}
                            onSourceChange={this.handleSourceChange}
                        />
                    </div>
                </div>

                {!leadsLoading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to send campaigns.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!leadsLoading && leadsError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{leadsError}</span>
                        <button className="outreach-banner-action" onClick={this.loadLeads}>
                            Retry
                        </button>
                    </div>
                )}

                {leadsLoading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!leadsLoading && !leadsError && !needsAuth && leads.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No leads found.
                    </div>
                )}

                {!leadsLoading && !leadsError && !needsAuth && leads.length > 0 && (
                    <div className="mail-layout">
                        <div className="mail-list-column">
                            {this.renderListToolbar()}
                            {this.renderLeadsList()}
                            {this.renderPagination()}
                        </div>
                        {this.renderCampaignPanel()}
                    </div>
                )}
            </div>
        )
    }
}

export { ComposeCampaignPage }